import React, { Fragment, useState } from 'react'
import styled from 'styled-components'
import parse from 'html-react-parser'
import {
  Section,
  VideoThumbnail,
  VideoInModal,
  ImageInModal,
} from 'fbk-components'
import { formatPublicDate } from 'fbk-utils'
import { GoBack } from 'components'
import { NewsArticle, ContentBlock } from 'types'
import { spacings, width } from 'theme'
import { Nav } from 'components/GoBack'
import { Lens as LensIcon, Quote } from 'assets/svg'
import Gallery from './Gallery'
import SharingAside from './SharingAside'

interface Props {
  content: NewsArticle
  goBackProps: Nav
}

const Wrapper = styled(Section)`
  padding-top: 40px;
  margin-top: 60px;

  .go-back {
    display: none;
  }

  .inner {
    ${width('contentWidth')}
  }

  @media (min-width: 768px) {
    margin-top: 80px;

    .go-back {
      display: inline-block;
      margin-bottom: 32px;
      color: ${({ theme }) => theme.colors.grey};
    }
  }

  @media (min-width: 1200px) {
    .inner {
      position: relative;
      margin: -60px auto 0;
    }
  }
`

const Content = styled.div`
  > p,
  h2,
  h3 {
    max-width: 688px;
  }

  h1 p {
    font-size: 30px;
    line-height: 34px;
  }

  .date {
    margin: 8px 0 40px;
  }

  h2 {
    margin: 32px 0 16px;
    font-size: 24px;
    line-height: 28px;
  }

  h3 {
    margin: 24px 0 8px;
    font-size: 20px;
    line-height: 24px;
  }

  .blockquote {
    padding-bottom: 16px;
    margin: 32px 0;
    font-size: 24px;
    font-weight: bold;
    line-height: 28px;
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
    ${spacings('padding-left', '2xs')}

    .author {
      margin-top: 16px;
    }

    .qoute-icon {
      width: 48px;
      height: 38px;
      margin-bottom: 16px;
    }
  }

  a {
    color: ${({ theme }) => theme.colors.primary};

    :hover {
      filter: brightness(60%);
    }
  }

  .photo-wrapper {
    position: relative;
    display: inline-flex;
    cursor: pointer;
  }

  .photo {
    display: block;
    width: 100%;
    height: 100%;
    min-height: auto;
    margin: 24px 0;
  }

  .lens {
    position: absolute;
    top: calc(50% - 20px);
    right: calc(50% - 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #fff;
    opacity: 0;
    transition: 0.2s ease-in-out;
  }

  .photo-desc {
    margin: -20px 0 24px;
  }

  > div:last-child {
    margin-bottom: 0;
  }

  @media (min-width: 768px) {
    h1 p {
      font-size: 44px;
      line-height: 48px;
    }

    h2 {
      margin-top: 40px;
      font-size: 32px;
      line-height: 36px;
    }

    h3 {
      margin-top: 32px;
    }

    .blockquote {
      margin: 40px 0;
    }

    .photo-wrapper:hover {
      .lens {
        opacity: 1;
      }
    }

    .photo {
      margin: 32px 0;
    }

    .photo-desc {
      margin: -24px 0 24px;
    }
  }

  @media (min-width: 1200px) {
    > p,
    h2,
    h3 {
      max-width: 660px;
    }
  }
`

const StyledVideoThumbnail = styled(VideoThumbnail)`
  margin: 32px 0;
`

const StyledVideoInModal = styled(VideoInModal)`
  background: #000;

  .close {
    position: fixed;
    top: 18px;
    right: 16px;
    width: 24px;
    height: 24px;
    background: none;
  }

  @media (min-width: 768px) {
    background: rgba(0, 0, 0, 0.85);

    .close {
      position: absolute;
      top: calc(-24px - 24px);
      right: 0;
    }
  }

  @media (min-width: 1200px) {
    .close {
      top: 0;
      right: calc(-24px - 36px);
    }
  }
`

const StyledImageInModal = styled(ImageInModal)`
  background: rgba(0, 0, 0, 0.85);

  .modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    max-width: 100vw;
    background: none;
  }

  .wrapper {
    height: 100%;
    max-height: 100vh;
  }

  .button-wrap {
    width: 60px;
    height: 60px;
    padding: 0;
    background: rgba(0, 0, 0, 0.8);

    button {
      width: inherit;
      height: inherit;
    }
  }
`

const Article = ({ content, goBackProps }: Props) => {
  const [activeVideo, setVideo] = useState<string | null>(null)
  const [activeImage, setImage] = useState<string | null>(null)

  const { heading, meta } = content

  const { date, year } = formatPublicDate(meta.published_at)

  const renderBlock = ({ type, value }: ContentBlock) => {
    switch (type) {
      case 'video':
        const url = typeof value === 'string' ? value : ''

        return (
          <>
            <StyledVideoThumbnail url={url} openVideo={() => setVideo(url)} />
          </>
        )

      case 'quote':
        return (
          <blockquote className="blockquote">
            <Quote className="qoute-icon" />
            {parse(value.text)}
            <p className="author g-light-text g-small-text">{value.author}</p>
          </blockquote>
        )

      case 'photo':
        return (
          <>
            <div
              className="photo-wrapper"
              onClick={() => setImage(value.image)}
            >
              <img
                className="photo"
                src={value.image}
                alt={value.description}
              />
              <div className="lens">
                <LensIcon />
              </div>
            </div>
            {value.description && (
              <p className="g-small-text g-light-text photo-desc">
                {value.description}
              </p>
            )}
          </>
        )

      case 'gallery':
        return <Gallery images={value} />

      default:
        return typeof value === 'string' ? parse(value) : null
    }
  }

  return (
    <>
      <Wrapper space="xl">
        <SharingAside />
        <div className="inner">
          <div className="go-back">
            <GoBack {...goBackProps} />
          </div>
          <Content>
            <h1
              className="title"
              dangerouslySetInnerHTML={{ __html: heading }}
            />
            <p className="g-light-text g-small-text date">
              {date} {year}
            </p>
            {content.content.map((block, index) => (
              <Fragment key={index}>{renderBlock(block)}</Fragment>
            ))}
          </Content>
        </div>
      </Wrapper>
      <StyledImageInModal
        closeIconSrc="/icons/close.svg"
        url={activeImage || ''}
        isModalOpen={!!activeImage}
        closeImage={() => setImage(null)}
      />
      <StyledVideoInModal
        closeIconSrc="/icons/close.svg"
        url={activeVideo || ''}
        isModalOpen={!!activeVideo}
        closeVideo={() => setVideo(null)}
      />
    </>
  )
}

export default Article
